import { MouseEventHandler } from 'react'
import {
  Box,
  Chip,
  Stack,
  Typography,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { WVerifyBadgeIcon } from '@weblite/icons'
import { UserListItemSkeleton } from './user-list-item-skeleton'
import { getUserDisplayName, useUser } from '$logic/users'
import { getClientUserId } from '$logic/client-user'
import { nothing } from '$func/utils'

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const StyledListItem = styled(ListItem)(() => ({
  height: 56,
  minHeight: 56,
  textAlign: 'right',
}))

interface StyledListItemButtonProps {
  hasActionComponent: boolean
  disableRipple: boolean
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'hasActionComponent',
})<StyledListItemButtonProps>`
  height: 100%;
  box-sizing: border-box;
  padding: 0 15px 0 ${props => (props.hasActionComponent ? 0 : 10)}px;
  overflow: hidden;
`

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 20,
  margin: '0 5px 2px 0',

  backgroundColor: theme.palette.secondary.light,
  '.MuiChip-label': {
    color: theme.palette.primary.main,
    ...theme.typography.caption1,
  },
}))

interface Props {
  userId: string | undefined
  avatarComponent: JSX.Element
  role?: string
  primaryText?: JSX.Element
  secondaryActionComponent?: JSX.Element
  secondaryText?: string | JSX.Element
  hasDivider?: boolean
  isTransparent?: boolean
  onClick?: () => unknown
  onContextMenu?: unknown
  isPassive?: boolean
  fullOpacity?: boolean
}

export function UserListItemBase({
  userId,
  avatarComponent,
  role = '',
  primaryText,
  secondaryActionComponent,
  secondaryText,
  hasDivider = true,
  isTransparent = false,
  onClick = nothing,
  isPassive: disabled = false,
  onContextMenu = nothing,
  fullOpacity = false,
}: Props) {
  const { user, err, loading } = useUser(userId)

  if (err) return <div>error</div>
  if (loading) return <UserListItemSkeleton />

  const isPassive = disabled || userId === getClientUserId()

  return (
    <>
      <StyledListItem
        disablePadding
        sx={{
          bgcolor: isTransparent ? 'transparent' : 'white',
          opacity: fullOpacity ? 1 : isPassive ? 0.5 : 1,
        }}
      >
        <StyledListItemButton
          hasActionComponent={!!secondaryActionComponent}
          disableRipple={isPassive}
          onClick={isPassive ? nothing : onClick}
          onContextMenu={
            isPassive
              ? nothing
              : (onContextMenu as MouseEventHandler<HTMLDivElement>)
          }
        >
          <ListItemAvatar>{avatarComponent}</ListItemAvatar>

          <Stack
            justifyContent="center"
            sx={{
              width: '100%',
              overflow: 'hidden',
              textAlign: 'right',
            }}
          >
            <Box
              sx={{ display: 'flex', overflow: 'hidden', marginBottom: '1px' }}
            >
              {primaryText || (
                <Typography
                  variant="subtitle3"
                  noWrap
                  color={isTransparent ? 'white' : undefined}
                >
                  {getUserDisplayName(user)}
                </Typography>
              )}

              {!!role && <StyledChip label={role} size="small" />}
              {user?.verified && (
                <WVerifyBadgeIcon
                  sx={{ color: 'primary500', width: '20px', height: '20px' }}
                />
              )}
            </Box>

            {typeof secondaryText === 'string' ? (
              <Typography
                variant="caption1"
                noWrap
                sx={{ color: !isTransparent ? '#9DA3AA' : 'white' }}
              >
                {secondaryText}
              </Typography>
            ) : (
              secondaryText
            )}
          </Stack>
        </StyledListItemButton>

        {secondaryActionComponent}
      </StyledListItem>

      {hasDivider && <StyledDivider variant="inset" />}
    </>
  )
}
